import React, {
  Fragment,
  memo,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { IconChevronLeft, IconEye } from '@app/icons';
import {
  dateFormat,
  moneyFormat,
  hidePhoneNumbers,
  generateReadableNumber,
} from '@app/helpers';
import { useNavigate } from 'react-router-dom';
import { MeasureLabelsShort, Vehicle, VehicleStatusNames } from '@app/models';
import placeholderImage from 'assets/images/vehicle_fallback.png';
import {
  FilePicker,
  Info,
  LoadableImage,
  StarRating,
  Tabs,
} from '@app/components';
import Reviews from '../Reviews/Reviews';
import { useUserContext } from '@app/providers';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Separator } from 'components/ui/separator';

interface Props {
  vehicle: Vehicle;
  actions?: ReactNode[];
  viewReplies?: boolean;
  editable?: boolean;
}

function VehicleInfo(props: Props & PropsWithChildren) {
  const {
    vehicle,
    actions = [],
    viewReplies = false,
    editable = false,
  } = props;
  const { user: authUser } = useUserContext();
  const navigate = useNavigate();

  const onClickBack = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(-1);
    },
    [navigate]
  );

  const vehicleImages = useMemo(() => {
    if (!vehicle?.imagesIds) {
      return [];
    }
    return vehicle.imagesIds.split(',');
  }, [vehicle?.imagesIds]);

  const tabLabels = useMemo(() => {
    const returnTabLabels = ['Общая информация'];
    if (authUser.authenticated) {
      returnTabLabels.push('Документы и Файлы');
    }
    return [...returnTabLabels, 'Отзывы'];
  }, [authUser.authenticated]);

  const tabContent = useMemo<ReactNode[]>(() => {
    const returnTabContent = [
      <div key="info" className="space-y-4">
        <Info label="Регион" value={vehicle?.kato?.nameRu || 'Не указано'} />
        <Info
          label="Описание"
          value={
            authUser.authenticated
              ? vehicle?.description || 'Не указано'
              : hidePhoneNumbers(vehicle?.description || '')
          }
        />
      </div>,
    ];

    if (authUser.authenticated) {
      returnTabContent.push(
        <FilePicker
          key="files"
          loadableFiles={vehicle?.filesIds ? vehicle.filesIds.split(',') : []}
          onChange={() => ''}
          editable={editable}
        />
      );
    }

    return [
      ...returnTabContent,
      <Reviews
        key="reviews"
        vehicleId={vehicle?.id}
        showButton={vehicle?.ownerId !== authUser?.id}
      />,
    ];
  }, [authUser, editable, vehicle]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4">
      <Card className="bg-white shadow-sm hover:shadow-md transition-shadow">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onClickBack}
              className="w-12 h-12 rounded-full p-0 hover:bg-gray-100"
            >
              <IconChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-2xl lg:text-3xl font-semibold truncate">
                {vehicle?.name || 'Без названия'}
              </CardTitle>
            </div>
            {vehicle?.status && (
              <Badge className="bg-green-600 text-white">
                {VehicleStatusNames[vehicle.status]}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <span className="font-medium">Тип:</span>
              <span>Предложение</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Категория:</span>
              <span>Спецтехника</span>
            </div>
            {vehicle?.rating && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Рейтинг:</span>
                <StarRating
                  rating={vehicle.rating}
                  reverse={false}
                  marginSize={14}
                />
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-white shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
            <div className="w-full">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <LoadableImage
                  imageIds={vehicleImages}
                  placeholder={placeholderImage}
                />
              </div>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Info
                  label="Номер объявления"
                  value={
                    vehicle?.id
                      ? generateReadableNumber(vehicle.id)
                      : 'Не указано'
                  }
                />
                <Info
                  label="Дата пуыбликации"
                  value={
                    vehicle?.createdAt
                      ? dateFormat(vehicle.createdAt)
                      : 'Не указано'
                  }
                />
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-blue-700">
                  {vehicle?.price ? moneyFormat(vehicle.price) : '0'}₸
                  {vehicle?.measure && (
                    <span className="text-base font-normal text-gray-500 ml-1">
                      / {MeasureLabelsShort[vehicle.measure]}
                    </span>
                  )}
                </h3>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <IconEye className="w-4 h-4" />
            <span>Просмотрели объявление: 15</span>
          </div>

          <Separator className="my-6" />
          <Tabs labels={tabLabels}>
            {tabContent.map((elem, index) => (
              <Fragment key={index}>{elem}</Fragment>
            ))}
          </Tabs>
          {actions.length > 0 && (
            <>
              <Separator className="my-6" />
              <div className="flex flex-wrap items-center justify-end gap-2">
                {actions.map((action, actionIndex) => (
                  <Fragment key={actionIndex}>{action}</Fragment>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default memo(VehicleInfo);
